import { User } from "lucide-react";

import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from "@/components/ui/avatar";
import type { TaskAssignee } from "@/lib/board";

/**
 * Overlapping stack of a task's assignees — shows up to `max`, then a "+N" bubble
 * for the rest (its title lists the overflow). Empty → a single "Unassigned"
 * placeholder, matching the old single-avatar card. `size` tracks Avatar's sizes.
 */
export function AssigneeAvatars({
  assignees,
  size = "sm",
  max = 3,
  className,
}: {
  assignees: TaskAssignee[];
  size?: "sm" | "default" | "lg";
  max?: number;
  className?: string;
}) {
  if (assignees.length === 0) {
    return (
      <Avatar size={size} className={className} title="Unassigned">
        <AvatarFallback>
          <User className="size-3.5 text-muted-foreground" />
        </AvatarFallback>
      </Avatar>
    );
  }

  const shown = assignees.slice(0, max);
  const extra = assignees.slice(max);

  return (
    <AvatarGroup className={className}>
      {shown.map((a) => (
        <Avatar key={a.id} size={size} title={a.name ?? "Assignee"}>
          {a.avatar && <AvatarImage src={a.avatar} alt={a.name ?? ""} />}
          <AvatarFallback>{a.initials ?? a.name?.[0] ?? "?"}</AvatarFallback>
        </Avatar>
      ))}
      {extra.length > 0 && (
        <AvatarGroupCount
          title={extra.map((a) => a.name ?? "Assignee").join(", ")}
        >
          +{extra.length}
        </AvatarGroupCount>
      )}
    </AvatarGroup>
  );
}
